"use client";

import { useEffect, useState } from "react";
import { getWorkerDistricts, getWorkerOccupations } from "./DropdownApiReq";
import { useTrainerContext } from "@/context/TrainerContext";
import Dropdown from "@/components/Dropdown";
import { usePathname, useSearchParams } from "next/navigation";
import apiFetch from "@/lib/fetch";
import { useRouter } from "next/navigation";
import { DROPDOWN_DEFAULT_VALUE_KEYS } from "@/@core/constants/dropdown";
import { useTranslation } from "@/hooks/translationContext";

export default function DropdownPart() {
  const { t } = useTranslation();
  const [selectedStreet, setSelectedStreet] = useState({ id: 0, title: t(DROPDOWN_DEFAULT_VALUE_KEYS.DISTRICT), slug: undefined });
  const [selectedJob, setSelectedJob] = useState({ id: 0, title: t(DROPDOWN_DEFAULT_VALUE_KEYS.OCCUPATION), slug: undefined });
  const [isStreetOpened, setIsStreetOpened] = useState(false);

  const { setTrainerFilters, trainerFilters } = useTrainerContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [streets, setStreets] = useState([]);
  const [occupations, setOccupations] = useState([]);

  useEffect(() => {
    const districtResponse = getWorkerDistricts();
    districtResponse.then((response) => setStreets(response));

    const occupationResponse = getWorkerOccupations();
    occupationResponse.then((response) => setOccupations(response));
  }, []);

  useEffect(() => {
    const occupation = searchParams.get("occupation");
    const district = searchParams.get("district");
    const filters = { ...trainerFilters };

    const getOccupation = async () => {
      if (occupation) {
        const occupationData = await apiFetch({
          route: `occupation/${occupation}`,
        });
        setSelectedJob({
          id: occupationData?.id,
          title: occupationData.descriptions[0]?.title,
          slug: occupationData.descriptions[0]?.slug,
        });
        filters.occupationId = occupationData.id;
      }
    };

    const getDistrict = async () => {
      if (district) {
        const districtData = await apiFetch({ route: `district/${district}` });
        setSelectedStreet(districtData);
        filters.districtId = districtData.id;
      }
    };

    const fetchData = async () => {
      await Promise.all([getOccupation(), getDistrict()]);
      setTrainerFilters(filters);
    };

    fetchData();
  }, [searchParams]);

  const changeUrl = ({
    district,
    occupation,
  }: {
    district?: string;
    occupation?: string;
  }) => {
    const searchParams = new URLSearchParams();

    if (district) searchParams.append("district", district);
    if (occupation) searchParams.append("occupation", occupation);

    const queryString = searchParams.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname);
  };

  return (
    <div className="w-full mt-4 flex justify-center items-center">
      <div className="container flex md:justify-end sm:flex-nowrap sm:gap-4 gap-2 items-center">
        <Dropdown
          setIsOpened={setIsStreetOpened}
          title={selectedStreet?.title}
          parentClassName="md:!w-[278px] w-full"
        >
          <button
            role="button"
            className="flex items-start w-full px-2 font-bold py-3 rounded cursor-pointer w-full"
            onClick={() => {
              setSelectedStreet({ id: 0, title: t(DROPDOWN_DEFAULT_VALUE_KEYS.DISTRICT), slug: undefined });
              setTrainerFilters({
                ...trainerFilters,
                districtId: null,
              });
              changeUrl({ district: "", occupation: selectedJob?.slug });
            }}
          >
            <span className="text-sm text-slate-800 font-bold">Tümü</span>
          </button>

          {streets.map((str: { title: string; slug: string; id: number }) => {
            return (
              <button
                role="button"
                key={str.id}
                onClick={() => {
                  setSelectedStreet(str);

                  setTrainerFilters({
                    ...trainerFilters,
                    districtId: str.id,
                  });
                  changeUrl({ district: str.slug, occupation: selectedJob?.slug });
                }}
                className="w-full flex items-start px-2 py-3 rounded cursor-pointer w-full"
              >
                <span className="text-sm text-slate-800 font-bold">
                  {str.title}
                </span>
              </button>
            );
          })}
        </Dropdown>

        <Dropdown
          title={selectedJob?.title}
          parentClassName="md:!w-[278px] w-full"
          className={isStreetOpened ? "!z-0" : ""}
        >
          <button
            role="button"
            className="w-full flex items-start w-full px-2 border-b border-stone-50 py-3 rounded cursor-pointer w-full"
            onClick={() => {
              setSelectedJob({ id: 0, title: t(DROPDOWN_DEFAULT_VALUE_KEYS.OCCUPATION), slug: undefined });
              setTrainerFilters({
                ...trainerFilters,
                occupationId: null,
              });
              changeUrl({ occupation: "", district: selectedStreet?.slug });
            }}
          >
            <span className="text-sm text-slate-800 font-bold">Tümü</span>
          </button>

          {occupations.map(
            (occ: { title: string; slug: string; id: number }) => {
              return (
                <button
                  role="button"
                  key={occ.id}
                  onClick={() => {
                    setSelectedJob(occ);

                    setTrainerFilters({
                      ...trainerFilters,
                      occupationId: occ.id,
                    });
                    changeUrl({ occupation: occ.slug, district: selectedStreet?.slug });
                  }}
                  className="w-full flex items-start px-2 border-b border-stone-50 py-3 rounded cursor-pointer w-full"
                >
                  <span className="text-sm text-slate-800 font-bold">
                    {occ.title}
                  </span>
                </button>
              );
            },
          )}
        </Dropdown>
      </div>
    </div>
  );
}
